import {
	Component,
	AfterViewInit,
	Input,
	Output,
	EventEmitter,
	ViewChild,
	ElementRef,
	SimpleChanges,
	OnChanges,
	computed,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Modal } from 'bootstrap';

import { DateService } from '../date.service';
import { LoaderService } from '../loader/loader.service';
import { AuthService } from '@/auth/auth.service';
import { Permissions } from '../enums/Permissions';
import { TourismEventTransportTermSlotsStore } from '@/tourism-calendar/tourism-calendar-event-view/tourism-event-transport-term-slots-store';

const MARGIN = 7;

@Component({
	selector: 'app-events-timeline',
	templateUrl: './events-timeline.component.html',
	styleUrls: ['./events-timeline.component.css'],
})
export class EventsTimelineComponent implements AfterViewInit, OnChanges {
	@ViewChild('grid') gridObject: ElementRef;

	@Input() date_from: string;
	@Input() date_to: string;
	@Input() module: string;
	@Input() data = null;
	@Input() addLink: string;
	@Input() offset = 0;
	@Input() showFilters = true;

	@Output() eventClicked = new EventEmitter();
	@Output() filtersClicked = new EventEmitter();
	@Output() viewsClicked = new EventEmitter();

	hours: Array<string> = ['00:00', '06:00', '12:00', '18:00'];
	days: Array<string>;
	grid = {
		width: 0,
		height: 0,
		cell: {
			width: 40.25,
			height: 30,
			hours: 6,
			padding: 8, // padding: 3 left, 3 right, border: 1 left, 1 right
		},
		label: {
			width: 161,
			height: 30,
		},
		rows: 0,
		cols: 0,
		isMoveable: false,
		startX: 0,
		stopX: 0,
		hourWidth: 0,
		scrollbarWidth: 30,
	};
	gridData = [];
	settings = {
		calendarDate: null,
	};
	modal = {
		title: 'Loading...',
	};
	setDate = '';
	readonly isOnlyDispatcher = computed(() => {
		const enabledPermissions = Object.entries(this.auth.permissionsByCode()).filter((val) => val[1]);
		return enabledPermissions.length === 1 && enabledPermissions[0][0] === Permissions.DISPATCHER;
	});

	private readonly daysOfWeek = ['Sobota', 'Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek'];
	private scrollToId = '';

	constructor(
		private readonly loader: LoaderService,
		private readonly date: DateService,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly auth: AuthService,
		protected readonly transportTermSlotsStore: TourismEventTransportTermSlotsStore,
	) {
		this.route.fragment.subscribe((fragment: string) => {
			this.scrollToId = fragment;
		});
	}

	ngAfterViewInit(): void {
		this.gridSetSize();
	}

	ngOnChanges(changes: SimpleChanges) {
		this.days = this.getDatesBetween(this.date_from, this.date_to);
		if (changes.data != undefined && changes.data.currentValue) {
			this.gridSetSize();
			this.gridDraw();
		}
		if (changes.date_from != undefined && changes.date_from.currentValue) {
			this.gridDraw();
		}
	}

	private gridSetSize() {
		this.grid.height = window.innerHeight - this.gridObject.nativeElement.offsetTop;
		this.grid.width = window.innerWidth - this.gridObject.nativeElement.offsetLeft - this.grid.scrollbarWidth;
		// this.gridObject.nativeElement.style.height = this.grid.height + 'px';
		this.gridObject.nativeElement.style.width = this.grid.width + 'px';
		this.grid.hourWidth = this.grid.cell.width / this.grid.cell.hours;
	}

	dateMove(days) {
		this.date_from = this.addDays(this.date_from, days);
		this.date_to = this.addDays(this.date_to, days);
		this.dateChanged();
	}

	elementClicked(element) {
		this.eventClicked.emit(element);
	}

	filersClicked() {
		this.filtersClicked.emit(true);
	}

	viewClicked() {
		this.viewsClicked.emit(true);
	}

	calendarOpen() {
		new Modal(document.getElementById('calendarModal')).show();
	}

	calendarClose(): void {
		const modal = Modal.getInstance(document.getElementById('calendarModal'));
		if (!modal) {
			return;
		}
		modal.hide();
	}

	calendarSubmit() {
		this.calendarClose();
		this.setCalendarDate(this.setDate);
		this.loader.show();
		this.router.navigate([this.route.snapshot.url[0].path, this.setDate]);
	}

	mousedown(event) {
		if (document.elementFromPoint(event.pageX, event.pageY)?.classList.contains('tooltip-content')) {
			return;
		}
		this.grid.startX = event.pageX;
		this.grid.isMoveable = true;
	}

	touchstart(event) {
		if (
			document
				.elementFromPoint(event.touches[0].pageX, event.touches[0].pageY)
				.classList.contains('tooltip-content')
		) {
			return;
		}
		this.grid.startX = event.touches[0].pageX;
		this.grid.isMoveable = true;
	}

	mouseup(event) {
		this.grid.isMoveable = true;
		this.afterMouseEvent(event.pageX);
	}

	touchend(event) {
		this.grid.isMoveable = true;
		this.afterMouseEvent(this.grid.stopX);
	}

	touchmove(event) {
		this.grid.stopX = event.touches[0].pageX;
	}

	private gridDraw() {
		if (!this.data) {
			return;
		}
		let rows: number;
		let datas = [];
		this.gridData = [];
		this.data.forEach((element) => {
			rows = 0;
			datas = [];
			element.events.forEach((event) => {
				const thisEvent = this.gridDrawElement(event, element.events, event.vehicles);

				if (!thisEvent) {
					return;
				}

				event.pixelFrom = thisEvent.pixelFrom;
				event.pixelWidth = thisEvent.pixelWidth;
				event.row = thisEvent.row;
				if (thisEvent.row > rows) {
					rows = thisEvent.row;
				}
				datas.push(thisEvent.data);
			});
			this.gridData.push({
				tableElementId: 'tableElement' + element.id,
				height: ((rows + 1) * this.grid.label.height).toString(),
				name: element.name,
				items: datas,
				details: element?.details,
				background: element?.background,
				class: element.id == this.scrollToId ? 'lastEdited' : '',
			});
		});
		// scroll to last edited element
		if (!this.scrollToId) {
			return;
		}
		const setIntervalTimer = setInterval(() => {
			const lastEditedElement = document.getElementById('tableElement' + this.scrollToId);
			if (!lastEditedElement) {
				return;
			}
			window.scrollTo({
				top: lastEditedElement.getBoundingClientRect().top - 70,
				behavior: 'smooth',
			});
			clearInterval(setIntervalTimer);
		}, 500);
	}

	// eslint-disable-next-line  @typescript-eslint/no-explicit-any
	private gridDrawElement(element: any, allEvents: any, vehicles: any) {
		let vehicle = null;

		if (vehicles?.length) {
			// eslint-disable-next-line  @typescript-eslint/no-explicit-any
			const filteredVehicles = vehicles.filter((v: any) => v.vehicle_id === element.vehicle);

			if (filteredVehicles.length > 0) {
				vehicle = filteredVehicles[element?.count ?? 0];
			}
		}

		const dateStart = vehicle?.date_start ?? element.date_from;
		const dateStop = vehicle?.date_stop ?? element.date_to;

		let pixelFrom = Number(this.pixelsBetweenDates(this.date_from + ' 00:00:00', new Date(dateStart)).toFixed(0));
		let pixelWidth = Number(this.pixelsBetweenDates(new Date(dateStart), new Date(dateStop)).toFixed(0)) + MARGIN;

		pixelWidth -= this.grid.cell.padding;
		if (pixelWidth <= 8) {
			pixelWidth = this.grid.cell.padding;
		}
		const background: string = element.background ? element.background : '#000000';
		const foreground: string = this.colorIsDark(element.background) ? '#ffffff' : '#000000';
		const border: string = element.border ? '0 0 5px 5px ' + element.border : '';
		const icon: string = element.icon ? element.icon : '';
		if (pixelFrom < 0) {
			pixelWidth = pixelWidth - Math.abs(pixelFrom);
			if (pixelWidth < 0) {
				// event starts and ends outside the window
				return;
			}
			pixelFrom = 0;
		}
		if (pixelFrom >= this.grid.width - this.grid.label.width) {
			// event starts outside the window
			return false;
		}
		if (pixelFrom + pixelWidth > this.grid.width - this.grid.label.width) {
			// event cut event when ends outside the window
			pixelWidth = this.grid.width - this.grid.label.width - pixelFrom;
		}
		const row: number = this.howManyColisions(element.id, pixelFrom, pixelFrom + pixelWidth, allEvents);
		const marginTop: number = row * this.grid.label.height;
		const data = {
			id: element.id,
			name: element.name,
			slots: this.transportTermSlotsStore.slotMap.get(element.id),
			icon: icon,
			style: {
				'margin-left.px': pixelFrom,
				'margin-top.px': marginTop + 2,
				'width.px': pixelWidth,
				background: background,
				color: foreground,
				'box-shadow': border,
			},
			element: element,
		};
		return {
			data: data,
			row: row,
			pixelFrom: pixelFrom,
			pixelWidth: pixelWidth,
		};
	}

	private howManyColisions(elementId, pixelFrom: number, pixelEnd: number, allEvents): number {
		const colidedRows = [];
		let currentPixelFrom: number;
		let currentPixelEnd: number;
		allEvents.forEach((event) => {
			if (event.pixelFrom == undefined || event.pixelWidth == undefined) {
				return;
			}
			currentPixelFrom = event.pixelFrom;
			currentPixelEnd = event.pixelFrom + event.pixelWidth;
			if (currentPixelEnd < pixelFrom || pixelEnd < currentPixelFrom) {
				// not coliding
				return;
			}
			colidedRows[event.row] = true;
		});
		if (!colidedRows.length) {
			return 0;
		}
		let i: number;
		for (i = 0; i < colidedRows.length; ++i) {
			if (!colidedRows[i]) {
				return i;
			}
		}
		return i;
	}

	private pixelsBetweenDates(date1, date2): number {
		const diff: number = new Date(date2).getTime() - new Date(date1).getTime();
		const hoursDiff: number = +(diff / 36e5).toFixed(2);
		return +(this.grid.hourWidth * hoursDiff + this.offset).toFixed(2); // 6; // 11
	}

	private afterMouseEvent(stopX: number) {
		if (!this.grid.startX) {
			return;
		}
		const pixels = Math.abs(this.grid.startX - stopX);
		if (pixels < 150) {
			this.grid.startX = null;
			this.grid.isMoveable = true;
			return;
		}
		const direction = this.grid.startX > stopX ? 1 : -1;
		const days = Math.ceil(pixels / 161) * direction;
		this.date_from = this.addDays(this.date_from, days);
		this.date_to = this.addDays(this.date_to, days);
		this.dateChanged();
	}

	private setCalendarDate(date: string): void {
		this.settings.calendarDate = date;
		localStorage.setItem('pageSettings-' + this.module, JSON.stringify(this.settings));
	}

	private dateChanged(): void {
		this.setDate = this.date_from;
		this.setCalendarDate(this.setDate);
		this.router.navigate([this.route.snapshot.url[0].path, this.setDate], {
			queryParamsHandling: 'preserve',
		});
	}

	private addDays(date, days = 0) {
		date = new Date(date);
		date.setDate(date.getDate() + days);
		return this.date.toUtcDate(date);
	}

	private getDatesBetween(startDate, endDate) {
		const dates = [];
		let currentDate = new Date(startDate);
		let dayOfWeek;
		endDate = new Date(endDate);
		while (currentDate <= endDate) {
			currentDate = new Date(
				currentDate.getFullYear(),
				currentDate.getMonth(),
				currentDate.getDate() + 1, // Will increase month if over range
			);
			dates.push({
				date: currentDate.toISOString().split('T')[0],
				dayOfWeek: this.daysOfWeek[currentDate.getDay()],
			});
		}
		return dates;
	}

	private colorIsDark(color) {
		if (!color) {
			return false;
		}
		// Variables for red, green, blue values
		let r, g, b, hsp;
		// Hex --> RGB
		color = +('0x' + color.slice(1).replace(color.length < 5 && /./g, '$&$&'));
		r = color >> 16;
		g = (color >> 8) & 255;
		b = color & 255;
		// HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
		hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));
		// Using the HSP value, determine whether the color is light or dark
		if (hsp > 150) {
			return false;
		}
		return true;
	}

	dateToday() {
		this.calendarClose();
		if (this.setDate == this.date.now().date) {
			return;
		}
		this.setDate = this.date.now().date;
		this.setCalendarDate(this.setDate);
		this.router.navigate([this.route.snapshot.url[0].path, this.setDate]);
	}
}
